import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getUserData, getUserMfaOptionsWithCounts } from 'redux/reducers/users'
import { Card, ICardProps } from 'components/Card/Card'
import { Alert, Card as MCard } from '@cmsgov/ds-medicare-gov'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { MyLink } from 'components/MyLink'
import { useQueryParams } from 'hooks/useQueryParams'
import { useMemo } from 'react'
import { MfaOptionRemovedAlert } from './MfaOptionRemovedAlert'

const defaultCardProps: Partial<ICardProps> = {
  headingLevel: '3',
  className: 'my-u-mb--8'
}

export const MfaSettings = () => {
  // Misc.
  const { t } = useTranslation('accountSettings')
  const { errors } = useSelector(getUserData)

  // Helper components
  const WithServerError = () => (
    <Alert variation='warn'>
      <p className="ds-c-alert__text">{t('errors.mfa')}</p>
    </Alert>
  )

  // Return
  return (
    <div className="ds-l-row">
      <div className="ds-l-md-col--12 ds-l-lg-col--4">
        <h2 className="ds-text-heading--xl ds-u-margin-bottom--2">
          {t('paragraph.mfaTitle')}
        </h2>
        <p>{t('paragraph.mfaInfo')}</p>
      </div>
      <div className="ds-l-md-col--12 ds-l-lg-col--8" id='mfa-card'>
        {errors?.mfa ? <WithServerError /> : <WithoutServerError />}
      </div>
    </div>
  )
}

const WithoutServerError = () => {
  const addMfaToAccountUrl = useMedicareUrl(
    'https://www.medicare.gov/account/security-code/enroll/choose-your-method?lang=en-us',
    'https://www.medicare.gov/account/security-code/enroll/choose-your-method?lang=es-mx'
  )
  const addMfaUrl = useMedicareUrl(
    'https://www.medicare.gov/account/security-code/add-security-method?lang=en-us',
    'https://www.medicare.gov/account/security-code/add-security-method?lang=es-mx'
  )
  const editMfaUrlBase = useMedicareUrl(
    'https://www.medicare.gov/account/security-code/update-security-method/:uuid?Lang=en-us',
    'https://www.medicare.gov/account/security-code/update-security-method/:uuid?Lang=es-mx'
  )

  const userMfaOptionsWithCounts = useSelector(getUserMfaOptionsWithCounts)
  const mfaEmpty = userMfaOptionsWithCounts.length === 0
  const queryParams = useQueryParams()
  const updatedOptId = useMemo(() => queryParams.get('updated_mfa_method'), [queryParams])
  const optIsRemoved = updatedOptId && (
    userMfaOptionsWithCounts.length === 0 ||
    !userMfaOptionsWithCounts.some(option => option.id === updatedOptId)
  )

  const { t } = useTranslation('accountSettings')

  return (
    <>
    {optIsRemoved && <MfaOptionRemovedAlert />}
    {mfaEmpty && (
      <Card
        key="cardMfa"
        dataTestId="cardMfa"
        title={t('cards.mfa.emptyTitle')}
        buttonCallbackOrHref={addMfaToAccountUrl}
        editButtonAriaLabel={t('btns.addMfaAriaLabel')}
        buttonText={t('btns.addVerificationMethod')}
        buttonSiteActivityProps={{
          core: {
            component: 'Account Settings',
            elementId: 'accountsettings_addverification_click',
            coreClickData: {
              linkUrl: addMfaToAccountUrl,
              linkText: t('btns.addVerificationMethod')
            }
          }
        }}
        {...defaultCardProps}
      />
    )}
      {!mfaEmpty && (
        <MCard>
          {/* @ts-ignore - mfa can't be undefined here like ts suggests since we're checking !mfaEmpty */}
          {userMfaOptionsWithCounts.map(({ type, count, mfaChannel, id }) => {
            const mfaTypeLabel = t(`mfaTypeLabels.${type}`, { count })
            const editUrl = editMfaUrlBase.replace(':uuid', id)
            return (
              <Card
              key={id}
              dataTestId={`mfaOpt_${id}`}
              title={mfaTypeLabel}
              buttonCallbackOrHref={editUrl}
              buttonSiteActivityProps={{
                core: {
                  component: 'Account Settings',
                  coreClickData: {
                    linkUrl: editUrl,
                    linkText: t('btns.edit')
                  },
                  elementId: 'accountsettings_editverification_click'
                }
              }}
              buttonText={t('btns.edit')}
              displayValue={mfaChannel}
              editButtonAriaLabel={`${t('btns.edit')}: ${mfaTypeLabel}`}
              isStacked={true}
              animationSubject={true}
              animateOnLoad={updatedOptId === id}
              headingLevel="3"
              />
            )
          })}
          {/* Mimic stacked card multiple__item for simple styling */}
          <div className='my-c-card my-c-card-multiple__item'>
            <MCard className='my-u-py--7 my-u-px--12'>
              <div className='ds-l-row ds-u-align-items--center ds-u-justify-content--end'>
                <div className='ds-l-col--12 ds-u-display--flex ds-u-justify-content--end'>
                  <MyLink
                    href={addMfaUrl}
                    className='ds-c-button'
                    siteActivityProps={{
                      core: {
                        component: 'Account Settings',
                        elementId: 'accountsettings_addverification_click',
                        coreClickData: {
                          linkUrl: addMfaUrl,
                          linkText: t('btns.addVerificationMethod')
                        }
                      }
                    }}
                  >
                    {t('btns.addVerificationMethod')}
                  </MyLink>
                </div>
              </div>
            </MCard>
          </div>
        </MCard>
      )}
  </>
  )
}
